import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormGroup,
    FormControlLabel,
    Checkbox,
    TextField,
    Box,
    Typography,
    Divider,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    Close as CloseIcon,
    Print as PrintIcon,
    Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { ColumnConfig, HeaderFieldConfig } from "../utils/pdfGenerator";

interface PrintCustomizationDialogProps {
    open: boolean;
    onClose: () => void;
    columns: ColumnConfig[];
    headerFields: HeaderFieldConfig[];
    onPrint: (
        customizedColumns: ColumnConfig[],
        headerFields: HeaderFieldConfig[],
        includeShipping: boolean,
        showSummary: boolean,
    ) => void;
    onPreview?: (
        customizedColumns: ColumnConfig[],
        headerFields: HeaderFieldConfig[],
        includeShipping: boolean,
        showSummary: boolean,
    ) => void;
}

const PrintCustomizationDialog: React.FC<PrintCustomizationDialogProps> = ({
    open,
    onClose,
    columns,
    headerFields,
    onPrint,
    onPreview,
}) => {
    const [customizedColumns, setCustomizedColumns] = useState<ColumnConfig[]>(
        [],
    );
    const [customizedHeaderFields, setCustomizedHeaderFields] = useState<
        HeaderFieldConfig[]
    >([]);
    const [includeShipping, setIncludeShipping] = useState(false);
    const [showSummary, setShowSummary] = useState(true);

    useEffect(() => {
        if (open) {
            setCustomizedColumns(JSON.parse(JSON.stringify(columns)));
            setCustomizedHeaderFields(JSON.parse(JSON.stringify(headerFields)));
            setIncludeShipping(false);
            setShowSummary(true);
        }
    }, [open, columns, headerFields]);

    const handleToggleColumn = (index: number) => {
        const updated = [...customizedColumns];
        updated[index].enabled = !updated[index].enabled;
        setCustomizedColumns(updated);
    };

    const handleLabelChange = (index: number, newLabel: string) => {
        const updated = [...customizedColumns];
        updated[index].label = newLabel;
        setCustomizedColumns(updated);
    };

    const handleToggleHeaderField = (index: number) => {
        const updated = [...customizedHeaderFields];
        updated[index].enabled = !updated[index].enabled;
        setCustomizedHeaderFields(updated);
    };

    const handleSelectAll = () => {
        const updated = customizedColumns.map((col) => ({
            ...col,
            enabled: true,
        }));
        setCustomizedColumns(updated);
    };

    const handleDeselectAll = () => {
        const updated = customizedColumns.map((col) => ({
            ...col,
            enabled: false,
        }));
        setCustomizedColumns(updated);
    };

    const handleReset = () => {
        setCustomizedColumns(JSON.parse(JSON.stringify(columns)));
        setCustomizedHeaderFields(JSON.parse(JSON.stringify(headerFields)));
    };

    const handlePrint = () => {
        onPrint(
            customizedColumns,
            customizedHeaderFields,
            includeShipping,
            showSummary,
        );
        onClose();
    };

    const handlePreview = () => {
        if (onPreview) {
            onPreview(
                customizedColumns,
                customizedHeaderFields,
                includeShipping,
                showSummary,
            );
        }
    };

    const enabledCount = customizedColumns.filter((col) => col.enabled).length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pb: 1,
                }}
            >
                <Box>
                    <Typography variant="h6" component="div">
                        Customize Print Layout
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Select columns to include and customize their labels
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                {/* Header Fields Section */}
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Header Information
                </Typography>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    mb={1}
                >
                    Select which fields to show in the header
                </Typography>
                <FormGroup sx={{ mb: 2 }}>
                    {customizedHeaderFields.map((field, index) => (
                        <FormControlLabel
                            key={field.key}
                            control={
                                <Checkbox
                                    checked={field.enabled}
                                    onChange={() =>
                                        handleToggleHeaderField(index)
                                    }
                                    size="small"
                                />
                            }
                            label={field.label}
                            sx={{ mb: 0.5 }}
                        />
                    ))}
                </FormGroup>

                <Divider sx={{ my: 2 }} />

                {/* Columns Section */}
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Table Columns
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        {enabledCount} of {customizedColumns.length} columns
                        selected
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            size="small"
                            onClick={handleSelectAll}
                            variant="outlined"
                        >
                            Select All
                        </Button>
                        <Button
                            size="small"
                            onClick={handleDeselectAll}
                            variant="outlined"
                        >
                            Deselect All
                        </Button>
                        <Button
                            size="small"
                            onClick={handleReset}
                            variant="outlined"
                            color="secondary"
                        >
                            Reset
                        </Button>
                    </Box>
                </Box>

                <FormGroup>
                    {customizedColumns.map((column, index) => (
                        <Box
                            key={column.key}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 1.5,
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: column.enabled
                                    ? "action.hover"
                                    : "transparent",
                                border: "1px solid",
                                borderColor: column.enabled
                                    ? "primary.light"
                                    : "divider",
                                transition: "all 0.2s",
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={column.enabled}
                                        onChange={() =>
                                            handleToggleColumn(index)
                                        }
                                        color="primary"
                                    />
                                }
                                label=""
                                sx={{ m: 0 }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                value={column.label}
                                onChange={(e) =>
                                    handleLabelChange(index, e.target.value)
                                }
                                disabled={!column.enabled}
                                placeholder="Column Label"
                                variant="outlined"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        fontSize: "0.875rem",
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </FormGroup>

                <Divider sx={{ my: 2 }} />

                <Box
                    sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showSummary}
                                onChange={(e) =>
                                    setShowSummary(e.target.checked)
                                }
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2" fontWeight="medium">
                                    Show Summary Section
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    Uncheck to hide costs (useful when
                                    requesting quotes from suppliers)
                                </Typography>
                            </Box>
                        }
                        sx={{ mb: 1 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeShipping}
                                onChange={(e) =>
                                    setIncludeShipping(e.target.checked)
                                }
                                color="primary"
                                disabled={!showSummary}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2" fontWeight="medium">
                                    Include Shipping in Total
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    When unchecked, shipping cost will be
                                    excluded from the final total
                                </Typography>
                            </Box>
                        }
                    />
                </Box>

                {enabledCount === 0 && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: "warning.light",
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2" color="warning.dark">
                            Please select at least one column to print.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    Cancel
                </Button>
                {onPreview && (
                    <Tooltip title="Preview PDF before printing">
                        <Button
                            onClick={handlePreview}
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            disabled={enabledCount === 0}
                        >
                            Preview
                        </Button>
                    </Tooltip>
                )}
                <Button
                    onClick={handlePrint}
                    variant="contained"
                    startIcon={<PrintIcon />}
                    disabled={enabledCount === 0}
                >
                    Generate PDF
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PrintCustomizationDialog;
